import { Fragment } from "react";
import { ArrowRight, PlayCircle, MapPin, Calendar, Clock } from "lucide-react";
import Marquee from "react-fast-marquee";
import { HERO_STATS, TICKER_ITEMS, TEAM_NAME, TEAM_TAGLINE, NEXT_MATCH } from "@/data/teamData";

export default function Hero() {
  return (
    <section
      id="beranda"
      data-testid="hero-section"
      className="relative w-full min-h-[100vh] overflow-hidden pt-20"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://customer-assets.emergentagent.com/job_football-team-30s/artifacts/cuz3j7sx_NZphoto-2182.JPG"
          alt="The 30s FF Team"
          className="w-full h-full object-cover object-center"
        />
        {/* Overlay — gelap di kiri (untuk teks), terang di kanan (untuk foto tim) */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#050B14] via-[#050B14]/70 to-[#050B14]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center min-h-[calc(100vh-80px)] py-12">
        {/* Left: copy */}
        <div className="lg:col-span-7 space-y-8">
          {/* Pre-title */}
          <div className="inline-flex items-center gap-3 border border-[#E63946]/40 bg-[#E63946]/10 px-4 py-1.5">
            <span className="w-2 h-2 bg-[#E63946] rounded-full pulse-red" />
            <span className="text-xs font-semibold tracking-[0.3em] text-[#E63946]">
              MUSIM 2026 — LIVE
            </span>
          </div>

          {/* Title */}
          <h1
            data-testid="hero-title"
            className="font-display text-6xl sm:text-7xl lg:text-8xl text-white leading-[0.9] tracking-tight"
          >
            BUKAN SEKEDAR
            <br />
            <span className="text-[#E63946]">MAIN BOLA.</span>
            <br />
            INI {TEAM_NAME.toUpperCase()}.
          </h1>

          <p className="text-base sm:text-lg text-white/70 max-w-xl leading-relaxed font-body">
            {TEAM_TAGLINE} Tim fun football yang dibangun dari persaudaraan,
            ditempa di lapangan, dan dirayakan setiap minggu.
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap gap-4">
            <a
              href="#pertandingan"
              data-testid="hero-cta-matches"
              className="inline-flex items-center gap-2 bg-[#E63946] hover:bg-[#D62839] text-white px-7 py-4 font-bold tracking-wider text-sm transition-all hover:translate-x-1"
            >
              JADWAL PERTANDINGAN <ArrowRight size={18} />
            </a>
            <a
              href="https://youtu.be/k7hP561sNmk?si=MbjKdyPG-elOQehN"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="hero-cta-highlights"
              className="inline-flex items-center gap-2 border border-white/30 hover:border-white text-white px-7 py-4 font-bold tracking-wider text-sm transition-colors hover:bg-white/5"
            >
              <PlayCircle size={18} /> TONTON HIGHLIGHT
            </a>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-3 sm:gap-6 pt-6 border-t border-white/10 max-w-xl">
            {HERO_STATS.map((s, i) => (
              <div
                key={i}
                data-testid={`hero-stat-${i}`}
                className="text-left"
              >
                <div className="font-display text-3xl sm:text-4xl text-white leading-none">
                  {s.value}
                </div>
                <div className="text-[10px] sm:text-xs text-white/50 tracking-[0.2em] mt-2">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Next Match preview card */}
        <div className="lg:col-span-5 flex justify-center lg:justify-end">
          <div
            data-testid="hero-next-match-card"
            className="w-full max-w-sm bg-[#0A1128]/90 backdrop-blur border border-white/10 p-6 relative"
          >
            <div className="absolute -top-3 left-6 bg-[#E63946] text-white text-[10px] font-bold tracking-[0.2em] px-3 py-1">
              MATCHDAY BERIKUTNYA
            </div>
            <div className="flex items-center justify-between gap-2 mb-4 mt-2">
              <span className="text-xs text-white/50 tracking-wider">
                {NEXT_MATCH.competition}
              </span>
              <span className="text-[9px] font-bold tracking-[0.2em] bg-[#E63946]/20 text-[#E63946] border border-[#E63946]/40 px-2 py-0.5">
                {NEXT_MATCH.teams.length} TIM
              </span>
            </div>

            <div className="flex items-start justify-between py-4">
              {NEXT_MATCH.teams.map((team, i) => (
                <Fragment key={i}>
                  <div className="flex flex-col items-center gap-2 flex-1 min-w-0 px-1">
                    {team.logo ? (
                      <img
                        src={team.logo}
                        alt={team.name}
                        className="w-14 h-14 object-contain"
                      />
                    ) : (
                      <div className="w-14 h-14 bg-white/10 border border-white/20 flex items-center justify-center font-display text-sm">
                        {team.short}
                      </div>
                    )}
                    <span className="text-[11px] font-semibold text-white text-center leading-tight">
                      {team.name.toUpperCase()}
                    </span>
                  </div>
                  {i < NEXT_MATCH.teams.length - 1 && (
                    <span className="font-display text-2xl text-white/40 h-14 flex items-center shrink-0">VS</span>
                  )}
                </Fragment>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-white/10 space-y-2">
              <div className="flex items-center gap-2 text-xs text-white/80">
                <Calendar size={13} className="text-[#E63946]" />
                <span className="font-semibold">{NEXT_MATCH.dateLabel}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-white/80">
                <MapPin size={13} className="text-[#E63946]" />
                <span>{NEXT_MATCH.venue}</span>
              </div>
            </div>

            <a
              href="#pertandingan"
              data-testid="hero-card-cta"
              className="mt-5 flex items-center justify-center gap-2 bg-[#E63946] hover:bg-[#D62839] text-white py-2.5 text-xs font-bold tracking-wider transition-colors"
            >
              LIHAT DETAIL LAGA <ArrowRight size={13} />
            </a>
          </div>
        </div>
      </div>

      {/* Bottom ticker bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-[#E63946] py-3 z-10">
        <Marquee speed={45} gradient={false} pauseOnHover>
          {TICKER_ITEMS.concat(TICKER_ITEMS).map((item, i) => (
            <span
              key={i}
              className="text-white text-xs sm:text-sm font-bold tracking-[0.2em] mx-10"
            >
              <span className="mr-10 text-white/70">●</span>
              {item}
            </span>
          ))}
        </Marquee>
      </div>
    </section>
  );
}
